const CART_DRAWER_GSAP_DEFAULTS = {
  duration: {
    overlay: 0.4,
    drawerEnter: 0.72,
    drawerExit: 0.52,
    drawerContent: 0.46,
  },
  ease: {
    in: 'power2.in',
    out: 'power2.out',
    emphasisOut: 'power3.out',
    emphasisInOut: 'power3.inOut',
  },
  stagger: {
    drawerItems: 0.055,
  },
};

function getCartDrawerMotionConfig() {
  const globalTokens = window.KM_GSAP_TOKENS || {};
  const globalDuration = globalTokens.duration || {};
  const globalEase = globalTokens.ease || {};
  const globalStagger = globalTokens.stagger || {};
  return {
    duration: {
      ...CART_DRAWER_GSAP_DEFAULTS.duration,
      overlay: globalDuration.overlay ?? CART_DRAWER_GSAP_DEFAULTS.duration.overlay,
      drawerEnter: globalDuration.drawerEnter ?? CART_DRAWER_GSAP_DEFAULTS.duration.drawerEnter,
      drawerExit: globalDuration.drawerExit ?? CART_DRAWER_GSAP_DEFAULTS.duration.drawerExit,
      drawerContent: globalDuration.drawerContent ?? CART_DRAWER_GSAP_DEFAULTS.duration.drawerContent,
    },
    ease: {
      ...CART_DRAWER_GSAP_DEFAULTS.ease,
      ...globalEase,
    },
    stagger: {
      ...CART_DRAWER_GSAP_DEFAULTS.stagger,
      drawerItems: globalStagger.drawerItems ?? CART_DRAWER_GSAP_DEFAULTS.stagger.drawerItems,
    },
  };
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.gsapTimeline = null;
    this.isCartDrawerOpen = false;
    this.overlay = this.querySelector('#CartDrawer-Overlay');
    this.drawerInner = this.querySelector('.drawer__inner');
    this.overlayClickHandler = this.close.bind(this);
    this.headingAlignmentRaf = null;
    this.logoRevealTimeout = null;
    this.onViewportResize = () => {
      if (!this.isCartDrawerOpen) return;
      this.scheduleHeadingAlignment();
    };

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.overlay?.addEventListener('click', this.overlayClickHandler);
    window.addEventListener('resize', this.onViewportResize, { passive: true });
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    this.overlay?.removeEventListener('click', this.overlayClickHandler);
    window.removeEventListener('resize', this.onViewportResize);
    if (this.headingAlignmentRaf) {
      cancelAnimationFrame(this.headingAlignmentRaf);
      this.headingAlignmentRaf = null;
    }
    if (this.logoRevealTimeout) {
      clearTimeout(this.logoRevealTimeout);
      this.logoRevealTimeout = null;
    }
    this.cartLinkObserver?.disconnect();
    document.body.classList.remove('is-cart-drawer-open');
  }

  get gsap() {
    return window.gsap;
  }

  canAnimateWithGsap() {
    if (!this.gsap || !this.overlay || !this.drawerInner) return false;
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  trapDrawerFocus() {
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.drawerInner;
    if (containerToTrapFocusOn && focusElement) {
      trapFocus(containerToTrapFocusOn, focusElement);
    }
  }

  setHeaderCartIconAccessibility() {
    if (this.headerCartLink && this.cartLinkClickHandler) {
      this.headerCartLink.removeEventListener('click', this.cartLinkClickHandler);
    }
    if (this.headerCartLink && this.cartLinkKeydownHandler) {
      this.headerCartLink.removeEventListener('keydown', this.cartLinkKeydownHandler);
    }

    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    this.headerCartLink = cartLink;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    this.cartLinkObserver?.disconnect();

    this.cartLinkClickHandler = (event) => {
      event.preventDefault();
      if (this.classList.contains('active')) {
        this.close();
        return;
      }
      this.open(cartLink);
    };
    this.cartLinkKeydownHandler = (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        if (this.classList.contains('active')) {
          this.close();
          return;
        }
        this.open(cartLink);
      }
    };

    cartLink.addEventListener('click', this.cartLinkClickHandler);
    cartLink.addEventListener('keydown', this.cartLinkKeydownHandler);

    this.cartLinkObserver = new MutationObserver(() => {
      this.updateHeaderCartButtonState(this.isCartDrawerOpen, { animate: false });
    });
    this.cartLinkObserver.observe(cartLink, { childList: true, subtree: true });

    this.updateHeaderCartButtonState(this.isCartDrawerOpen, { animate: false });
  }

  updateHeaderCartButtonState(isOpen, { animate = true } = {}) {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    const label = cartLink.querySelector('.header__cart-label');
    const count = cartLink.querySelector('.header__cart-count');
    if (!label) return;

    if (!cartLink.dataset.cartLabelDefault) {
      cartLink.dataset.cartLabelDefault = label.textContent.trim() || 'CART';
    }

    const defaultLabel = cartLink.dataset.cartLabelDefault;
    const nextLabel = isOpen ? 'CLOSE' : defaultLabel;
    this.updateHeaderCartLabelWithSlide(label, nextLabel, animate);

    if (count) {
      count.hidden = isOpen;
    }

    cartLink.classList.toggle('is-cart-drawer-open', isOpen);
    cartLink.setAttribute('aria-label', isOpen ? 'Close cart drawer' : 'Open cart drawer');
    cartLink.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (isOpen) {
      this.scheduleHeadingAlignment();
    }
  }

  scheduleHeadingAlignment() {
    if (this.headingAlignmentRaf) {
      cancelAnimationFrame(this.headingAlignmentRaf);
      this.headingAlignmentRaf = null;
    }

    this.headingAlignmentRaf = requestAnimationFrame(() => {
      this.syncHeadingAlignment();
      this.headingAlignmentRaf = requestAnimationFrame(() => {
        this.syncHeadingAlignment();
      });
    });
  }

  syncHeadingAlignment() {
    const cartLabel =
      document.querySelector('#cart-icon-bubble .header__cart-label') || document.querySelector('#cart-icon-bubble');
    const drawerHeading = this.querySelector('.drawer__heading');

    if (!cartLabel || !drawerHeading) {
      this.style.setProperty('--km-cart-heading-offset', '0px');
      return;
    }

    const cartTop = cartLabel.getBoundingClientRect().top;
    const drawerTop = drawerHeading.getBoundingClientRect().top;
    if (!Number.isFinite(cartTop) || !Number.isFinite(drawerTop)) {
      this.style.setProperty('--km-cart-heading-offset', '0px');
      return;
    }

    const rawOffset = cartTop - drawerTop;
    const clampedOffset = Math.max(-120, Math.min(120, rawOffset));
    this.style.setProperty('--km-cart-heading-offset', `${clampedOffset.toFixed(2)}px`);
  }

  getHeaderCartLabelTextNode(label) {
    let textNode = label.querySelector('.header__cart-label-text');
    if (!textNode) {
      const initialText = label.textContent.trim();
      label.textContent = '';
      textNode = document.createElement('span');
      textNode.className = 'header__cart-label-text';
      textNode.textContent = initialText;
      label.appendChild(textNode);
    }
    return textNode;
  }

  measureHeaderCartLabelWidth(label, text) {
    const probe = document.createElement('span');
    const computed = window.getComputedStyle(label);
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.whiteSpace = 'nowrap';
    probe.style.left = '-9999px';
    probe.style.top = '0';
    probe.style.fontFamily = computed.fontFamily;
    probe.style.fontSize = computed.fontSize;
    probe.style.fontWeight = computed.fontWeight;
    probe.style.fontStyle = computed.fontStyle;
    probe.style.letterSpacing = computed.letterSpacing;
    probe.style.lineHeight = computed.lineHeight;
    probe.style.textTransform = computed.textTransform;
    probe.textContent = text;
    document.body.appendChild(probe);
    const width = Math.ceil(probe.getBoundingClientRect().width);
    probe.remove();
    return width;
  }

  lockHeaderCartLabelWidth(label, currentLabel, nextLabel) {
    const width = Math.max(
      this.measureHeaderCartLabelWidth(label, currentLabel),
      this.measureHeaderCartLabelWidth(label, nextLabel),
      1
    );
    label.style.width = `${width}px`;
  }

  clearHeaderCartLabelWidth(label) {
    label.style.removeProperty('width');
  }

  updateHeaderCartLabelWithSlide(label, nextLabel, animate) {
    const textNode = this.getHeaderCartLabelTextNode(label);
    const currentLabel = textNode.textContent.trim();

    if (currentLabel === nextLabel) {
      label.dataset.text = currentLabel;
      label.classList.remove('is-word-sliding');
      label.classList.remove('is-word-snap');
      this.clearHeaderCartLabelWidth(label);
      return;
    }

    if (label._wordSlideTimeout) {
      clearTimeout(label._wordSlideTimeout);
      label._wordSlideTimeout = null;
    }
    if (label._wordSlideEndHandler) {
      textNode.removeEventListener('transitionend', label._wordSlideEndHandler);
      label._wordSlideEndHandler = null;
    }

    if (!animate) {
      label.classList.add('is-word-snap');
      textNode.textContent = nextLabel;
      label.dataset.text = nextLabel;
      label.classList.remove('is-word-sliding');
      label.offsetHeight;
      label.classList.remove('is-word-snap');
      this.clearHeaderCartLabelWidth(label);
      return;
    }

    const finish = () => {
      label.classList.add('is-word-snap');
      textNode.textContent = nextLabel;
      label.dataset.text = nextLabel;
      label.classList.remove('is-word-sliding');
      label.offsetHeight;
      label.classList.remove('is-word-snap');
      this.clearHeaderCartLabelWidth(label);
      if (label._wordSlideTimeout) {
        clearTimeout(label._wordSlideTimeout);
        label._wordSlideTimeout = null;
      }
      if (label._wordSlideEndHandler) {
        textNode.removeEventListener('transitionend', label._wordSlideEndHandler);
        label._wordSlideEndHandler = null;
      }
    };

    this.lockHeaderCartLabelWidth(label, currentLabel, nextLabel);
    label.dataset.text = nextLabel;
    label.classList.remove('is-word-sliding');
    label.classList.remove('is-word-snap');
    // Force reflow so removing/adding class restarts the slide transition.
    label.offsetHeight;
    label.classList.add('is-word-sliding');

    label._wordSlideEndHandler = (event) => {
      if (event.propertyName !== 'transform') return;
      finish();
    };
    textNode.addEventListener('transitionend', label._wordSlideEndHandler);
    label._wordSlideTimeout = window.setTimeout(finish, 700);
  }

  setHeaderLogoHiddenState(isHidden, { delay = 0 } = {}) {
    if (this.logoRevealTimeout) {
      clearTimeout(this.logoRevealTimeout);
      this.logoRevealTimeout = null;
    }

    if (delay > 0) {
      this.logoRevealTimeout = window.setTimeout(() => {
        document.body.classList.toggle('is-cart-drawer-open', isHidden);
        this.logoRevealTimeout = null;
      }, delay);
      return;
    }

    document.body.classList.toggle('is-cart-drawer-open', isHidden);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    document.body.classList.add('overflow-hidden');
    this.setHeaderLogoHiddenState(true);
    this.isCartDrawerOpen = true;
    this.updateHeaderCartButtonState(true, { animate: true });
    this.scheduleHeadingAlignment();

    if (this.canAnimateWithGsap()) {
      this.openWithGsap();
      return;
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener('transitionend', () => this.trapDrawerFocus(), { once: true });
    window.setTimeout(() => {
      if (this.classList.contains('active')) {
        this.trapDrawerFocus();
        this.scheduleHeadingAlignment();
      }
    }, 350);
  }

  openWithGsap() {
    const gsap = this.gsap;
    const motion = getCartDrawerMotionConfig();
    const animatedContent = this.getAnimatedContentElements();

    this.gsapTimeline?.kill();
    this.classList.add('animate', 'active', 'animate-gsap');
    gsap.set(this, { visibility: 'visible' });
    gsap.set(this.overlay, { autoAlpha: 0 });
    gsap.set(this.drawerInner, { xPercent: 100, force3D: true });
    if (animatedContent.length) {
      gsap.set(animatedContent, { autoAlpha: 0, force3D: true });
    }

    this.gsapTimeline = gsap.timeline({
      defaults: { overwrite: 'auto' },
      onComplete: () => {
        this.trapDrawerFocus();
        this.scheduleHeadingAlignment();
      },
    });

    this.gsapTimeline
      .to(this.overlay, {
        autoAlpha: 1,
        duration: motion.duration.overlay,
        ease: motion.ease.out,
      }, 0)
      .to(this.drawerInner, {
        xPercent: 0,
        duration: motion.duration.drawerEnter,
        ease: motion.ease.emphasisOut,
      }, 0);

    if (animatedContent.length) {
      this.gsapTimeline.to(animatedContent, {
        autoAlpha: 1,
        duration: motion.duration.drawerContent,
        ease: motion.ease.out,
        stagger: motion.stagger.drawerItems,
        clearProps: 'opacity,visibility,transform',
      }, 0.18);
    }
  }

  close() {
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.setHeaderLogoHiddenState(false, { delay: 180 });
    this.isCartDrawerOpen = false;
    this.updateHeaderCartButtonState(false, { animate: true });

    if (this.canAnimateWithGsap() && this.classList.contains('active')) {
      this.closeWithGsap();
      return;
    }

    this.classList.remove('active');
  }

  closeWithGsap() {
    const gsap = this.gsap;
    const motion = getCartDrawerMotionConfig();
    const animatedContent = this.getAnimatedContentElements();

    this.gsapTimeline?.kill();
    this.classList.add('animate-gsap');
    this.gsapTimeline = gsap.timeline({
      defaults: { overwrite: 'auto' },
      onComplete: () => {
        this.classList.remove('active');
        gsap.set(this, { visibility: 'hidden' });
        if (animatedContent.length) {
          gsap.set(animatedContent, { clearProps: 'opacity,visibility,transform' });
        }
      },
    });

    if (animatedContent.length) {
      this.gsapTimeline.to(animatedContent, {
        autoAlpha: 0,
        duration: Math.max(0.2, motion.duration.drawerContent * 0.65),
        ease: motion.ease.in,
        stagger: {
          each: motion.stagger.drawerItems,
          from: 'end',
        },
      }, 0);
    }

    this.gsapTimeline
      .to(this.overlay, {
        autoAlpha: 0,
        duration: motion.duration.overlay,
        ease: motion.ease.in,
      }, 0.06)
      .to(this.drawerInner, {
        xPercent: 100,
        duration: motion.duration.drawerExit,
        ease: motion.ease.emphasisInOut,
      }, 0.06);
  }

  getAnimatedContentElements() {
    const selectors = [
      '.drawer__header',
      '.cart-drawer__item-card',
      '.cart-drawer__summary-panel',
      '.drawer__footer > details',
    ];
    return Array.from(this.querySelectorAll(selectors.join(',')));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  refreshDomRefs() {
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
    this.overlay = this.querySelector('#CartDrawer-Overlay');
    this.drawerInner = this.querySelector('.drawer__inner');
    this.overlay?.addEventListener('click', this.overlayClickHandler);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.refreshDomRefs();

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
